import { useEffect, useState } from "react";

/**
 * Whether a CSS media query currently matches.
 *
 * Used where a layout decision cannot be expressed in CSS because the two
 * layouts are different *components* rather than different styles — a panel
 * beside the page on a wide screen versus a focus-managed sheet over it on a
 * narrow one. Hiding the wide layout with `display: none` looks the same and is
 * not the same: the content stays in the accessibility tree and in the tab
 * order, so a keyboard user reaches controls they cannot see.
 *
 * Returns `false` before the first effect runs (and in any non-DOM runtime), so
 * a caller should treat `false` as "not the special case" rather than as a
 * measurement.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const list = window.matchMedia(query);
    setMatches(list.matches);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    list.addEventListener("change", onChange);
    return () => list.removeEventListener("change", onChange);
  }, [query]);

  return matches;
}
